import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';
import {
  Box,
  SimpleGrid,
  Text,
  Button,
  useColorModeValue,
  useDisclosure,
  Icon,
  useToast,
  Flex,
  Heading,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  MdDevices,
  MdCheckCircle,
  MdCancel,
  MdAlarm,
  MdHistory,
} from 'react-icons/md';

import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import ComplexTable from 'views/admin/default/components/ComplexTable';
import EditDeviceDetailModal from 'components/modal/Modal';
import Sidebar from 'components/sidebar/Sidebar';
import { useDevices } from '../context/DeviceContext';
import GlobalModal from 'components/GlobalModal';
import { EditIcon } from '@chakra-ui/icons';
import BreadCrumb from 'components/breadCrumb/BreadCrumb';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export default function MainDashboard() {
  const breadcrumbItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'All Devices', path: null },
  ];

  const navigate = useNavigate();
  const toast = useToast();
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const userAuthority = localStorage.getItem('userAuthority');
  const customerId = localStorage.getItem('customerId');

  const {
    deviceData,
    loadingDevices,
    hasNext,
    totalPages,
    totalDevices,
    fetchAllDevices,
    fetchDeviceAttributes,
    fetchDeviceAssetId,
    fetchAssetAdditionalInfo,
    totalActiveDevices,
    totalInactiveDevices,
  } = useDevices();

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [savingDevice, setSavingDevice] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const sidebarExpandedWidth = '240px';
  const sidebarCollapsedWidth = '60px';
  const sidebarWidth = collapsed ? sidebarCollapsedWidth : sidebarExpandedWidth;

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyDevice, setHistoryDevice] = useState(null);

  const [assetSearch, setAssetSearch] = useState('');
  const [assetResults, setAssetResults] = useState([]);
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);
  const [assetSearchError, setAssetSearchError] = useState('');
  const [relationResponses, setRelationResponses] = useState({});
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [searchType, setSearchType] = useState('device');
  const [currentSearchValue, setCurrentSearchValue] = useState('');
  const debouncedAssetSearch = useDebounce(assetSearch, 400);

  const getAssetApi = (searchValue) => {
    return userAuthority !== 'TENANT_ADMIN'
      ? apiUrls.fetchAssetInfoforCustomer(0, 10, searchValue, customerId)
      : apiUrls.fetchAssetInfo(0, 10, searchValue);
  };

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  // Initial load
  useEffect(() => {
    fetchAllDevices('', 0);
    setCurrentPage(0);
  }, [fetchAllDevices]);

  const saveDeviceInfo = useCallback(
    async (updatedDevice) => {
      setSavingDevice(true);

      try {
        const warrantyDate =
          typeof updatedDevice.warranty === 'string'
            ? new Date(updatedDevice.warranty).getTime()
            : updatedDevice.warranty;

        const data = {
          warranty_date: warrantyDate,
          location: {
            lat: updatedDevice.location.lat,
            lang: updatedDevice.location.lng,
            loc: updatedDevice.location.loc || '',
          },
        };

        const response = await axiosInstance.post(
          apiUrls.updateDevice(updatedDevice.id),
          data,
        );

        if (response.status === 200) {
          toast({
            title: 'Device updated.',
            description:
              'The device information has been updated successfully.',
            status: 'success',
            duration: 4000,
            isClosable: true,
            position: 'center',
          });

          // Refresh current page
          if (searchType === 'asset' && assetSearch) {
            // If asset search is active, refresh the filtered results
            handleAssetSearchRefresh();
          } else if (currentSearchValue) {
            // If device search is active
            fetchAllDevices(currentSearchValue, currentPage);
          } else {
            // Normal pagination
            fetchAllDevices('', currentPage);
          }
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          toast({
            title: 'Error',
            description: 'Failed to update device info. Please try again.',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'center',
          });
        }
      } finally {
        setSavingDevice(false);
      }
    },
    [
      toast,
      fetchAllDevices,
      currentPage,
      currentSearchValue,
      searchType,
      assetSearch,
    ],
  );

  const handleSave = useCallback(
    (updatedDevice) => {
      saveDeviceInfo(updatedDevice);
      onClose();
    },
    [onClose, saveDeviceInfo],
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return 'NA';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const historyColumns = React.useMemo(
    () => [
      {
        id: 'timestamp',
        accessorKey: 'timestamp',
        header: () => <Text>Modified at</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: () => <Text>Action</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'group',
        accessorKey: 'group',
        header: () => <Text>Group</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'modbus_config',
        accessorKey: 'modbus_config',
        header: () => <Text>Modbus Config</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'modbus_version',
        accessorKey: 'modbus_version',
        header: () => <Text>Modbus Version</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'patch',
        accessorKey: 'patch',
        header: () => <Text>Patch</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'patch_version',
        accessorKey: 'patch_version',
        header: () => <Text>Patch Version</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'bin',
        accessorKey: 'bin',
        header: () => <Text>Bin</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'bin_version',
        accessorKey: 'bin_version',
        header: () => <Text>Bin Version</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
    ],
    [],
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'serialNumber',
        accessorKey: 'serialNumber',
        header: () => <Text>Serial Number</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'country',
        accessorKey: 'country',
        header: () => <Text>Country</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'state',
        accessorKey: 'state',
        header: () => <Text>State</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'noc',
        accessorKey: 'noc',
        header: () => <Text>Noc</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'plant',
        accessorKey: 'plant',
        header: () => <Text>Plant</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: () => <Text>Status</Text>,
        cell: (info) => {
          const value = info.getValue();
          const isActive = Boolean(value);
          return (
            <Text color={isActive ? 'green.400' : 'red.400'}>
              {isActive ? 'Active' : 'Inactive'}
            </Text>
          );
        },
      },
      {
        id: 'app_version',
        accessorKey: 'app_version',
        header: () => <Text>App Version</Text>,
        cell: (info) => <Text>{info.getValue() || '-'}</Text>,
      },
      {
        id: 'device_type',
        accessorKey: 'device_type',
        header: () => <Text>Device Type</Text>,
        cell: (info) => {
          const type = info.getValue();
          let label = '-';
          if (type === 1) label = 'MINI';
          else if (type === 2) label = 'GRIDX';
          else if (type === 3) label = 'MINI SECURE';
          return <Text>{label}</Text>;
        },
      },
      {
        id: 'modbus_config_name',
        accessorKey: 'modbus_config_name',
        header: () => <Text>Config Name</Text>,
        cell: (info) => {
          const configName = info.getValue() || '-';
          const configStatus = info.row.original.config_status || '-';
          return (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap={1}
            >
              <Text>{configName}</Text>
              <Button
                size="xs"
                variant="text"
                bg={
                  configStatus === 'Pending'
                    ? 'orange.400'
                    : configStatus === 'Applied'
                      ? 'green.500'
                      : 'yellow'
                }
                color="white"
                aria-label="Config Status"
              >
                {configStatus}
              </Button>
            </Box>
          );
        },
      },
      {
        id: 'patch_name',
        accessorKey: 'patch_name',
        header: () => <Text>Patch Name</Text>,
        cell: (info) => {
          const patchName = info.getValue() || '-';
          const patchStatus = info.row.original.patch_status || '-';
          return (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap={1}
            >
              <Text>{patchName}</Text>
              <Button
                size="xs"
                bg={
                  patchStatus === 'Pending'
                    ? 'orange.400'
                    : patchStatus === 'Applied'
                      ? 'green.500'
                      : 'yellow'
                }
                color="white"
                variant="text"
                aria-label="Patch Status"
              >
                {patchStatus}
              </Button>
            </Box>
          );
        },
      },
      {
        id: 'patch_v',
        accessorKey: 'patch_v',
        header: () => <Text>Patch Version</Text>,
        cell: (info) => {
          const value = info.getValue();
          return <Text>{value === 0 ? 0 : value || '-'}</Text>;
        },
      },
      {
        id: 'configure',
        accessorKey: 'details',
        header: () => <Text>Actions</Text>,
        cell: (info) => (
          <Flex gap={2}>
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderWidth="2px"
              cursor="pointer"
              disabled={loadingDevices}
              onClick={() => {
                try {
                  localStorage.setItem(
                    'selectedDeviceId',
                    String(info.row.original.deviceId),
                  );
                  localStorage.setItem(
                    'deviceId',
                    String(info.row.original.serialNumber),
                  );
                  localStorage.setItem(
                    'deviceType',
                    String(info.row.original.device_type),
                  );
                } catch (e) {
                  // ignore storage errors
                }

                navigate(`/device/${info.row.original.deviceId}/edit`, {
                  state: {
                    deviceId: info.row.original.serialNumber,
                  },
                });
              }}
            >
              <EditIcon />
            </Button>
            <Button
              size="sm"
              colorScheme="yellow"
              variant="outline"
              borderWidth="2px"
              cursor="pointer"
              onClick={() => {
                const historyArr =
                  (info.row.original.history &&
                  Array.isArray(info.row.original.history)
                    ? info.row.original.history
                    : []) || [];
                setHistoryData(historyArr);
                setHistoryDevice(info.row.original);
                setHistoryModalOpen(true);
              }}
              aria-label="View History"
            >
              <MdHistory />
            </Button>
          </Flex>
        ),
      },
    ],
    [loadingDevices, navigate],
  );

  // Statistics
  const activeDevices = deviceData.filter((d) => d.active).length;
  const inactiveDevices = totalDevices - activeDevices;
  const currentTime = Date.now();
  const warrantyExpired = deviceData.filter(
    (d) => d.warranty && d.warranty < currentTime,
  ).length;
  const warrantyActive = deviceData.filter(
    (d) => d.warranty && d.warranty >= currentTime,
  ).length;

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (searchType === 'asset' && assetSearch) {
      // For asset search, we don't support pagination yet
      // You can implement if needed
    } else if (currentSearchValue) {
      fetchAllDevices(currentSearchValue, page);
    } else {
      fetchAllDevices('', page);
    }
  };

  const fetchDevicesBySerials = async (serialNumbers) => {
    setFilteredDevices([]);
    setAssetSearchLoading(true);
    try {
      const filtered = deviceData.filter((d) =>
        serialNumbers.includes(d.serialNumber),
      );

      const deviceRows = await Promise.all(
        filtered.map(async (device) => {
          const attrs = await fetchDeviceAttributes(device.serialNumber);
          const assetId = await fetchDeviceAssetId(device.deviceId);
          let assetInfo = {};
          if (assetId) {
            assetInfo = await fetchAssetAdditionalInfo(assetId);
          }

          return {
            ...device,
            ...attrs,
            country: assetInfo?.CountryName || device.country || '-',
            state: assetInfo?.StateName || device.state || '-',
            noc: assetInfo?.NocName || device.noc || '-',
            plant: assetInfo?.DisplayName || device.plant || '-',
          };
        }),
      );

      setFilteredDevices(deviceRows.filter(Boolean));
    } finally {
      setAssetSearchLoading(false);
    }
  };

  const handleAssetSearchRefresh = async () => {
    if (!assetSearch) return;

    setAssetSearchLoading(true);
    try {
      const resp = await axiosInstance.get(getAssetApi(assetSearch));
      const assets = resp.data.data || [];
      setAssetResults(assets);

      const relationPromises = assets.map(async (asset) => {
        try {
          const relResp = await axiosInstance.get(
            apiUrls.relationResp(asset.id.id),
          );
          return { assetId: asset.id.id, data: relResp.data };
        } catch (err) {
          return { assetId: asset.id.id, data: null };
        }
      });

      const allRelations = await Promise.all(relationPromises);
      const relMap = {};
      allRelations.forEach((r) => {
        relMap[r.assetId] = r.data;
      });
      setRelationResponses(relMap);
    } catch (error) {
      setAssetSearchError('Error fetching asset info.');
    } finally {
      setAssetSearchLoading(false);
    }
  };

  useEffect(() => {
    if (assetResults.length > 0) {
      const serials = assetResults.flatMap((asset) => {
        const rels = relationResponses[asset.id.id] || [];
        return rels.map((r) => r.toName).filter(Boolean);
      });
      if (serials.length > 0) {
        fetchDevicesBySerials(serials);
      } else {
        setFilteredDevices([]);
      }
    } else {
      setFilteredDevices(deviceData);
    }
  }, [assetResults, relationResponses]);

  useEffect(() => {
    if (assetSearch === '') {
      fetchAllDevices(currentSearchValue, currentPage);
      setAssetResults([]);
      setRelationResponses({});
    }
  }, [assetSearch]);

  useEffect(() => {
    if (assetSearch === '') {
      setFilteredDevices(deviceData);
    }
  }, [deviceData, assetSearch]);

  const handleTableSearch = (value, type) => {
    setAssetSearchError('');
    setCurrentSearchValue(value);

    if (!value) {
      setAssetSearch('');
      setFilteredDevices(deviceData);
      setAssetResults([]);
      setRelationResponses({});
      setCurrentPage(0);
      fetchAllDevices('', 0);
      return;
    }

    if (type === 'asset') {
      setAssetSearch(value);
      setCurrentPage(0);
    } else {
      setAssetSearch('');
      setFilteredDevices(deviceData);
      setAssetResults([]);
      setRelationResponses({});
      setCurrentPage(0);
      fetchAllDevices(value, 0);
    }
  };

  useEffect(() => {
    if (searchType !== 'asset') {
      setAssetSearch('');
      setFilteredDevices(deviceData);
      setAssetResults([]);
      setRelationResponses({});
      return;
    }

    if (!debouncedAssetSearch) {
      setFilteredDevices(deviceData);
      setAssetResults([]);
      setRelationResponses({});
      return;
    }

    let cancelled = false;
    setAssetSearchLoading(true);
    setAssetSearchError('');

    (async () => {
      try {
        const resp = await axiosInstance.get(getAssetApi(debouncedAssetSearch));
        const assets = resp.data.data || [];
        if (cancelled) return;
        setAssetResults(assets);

        const relationPromises = assets.map(async (asset) => {
          try {
            const relResp = await axiosInstance.get(
              apiUrls.relationResp(asset.id.id),
            );
            return { assetId: asset.id.id, data: relResp.data };
          } catch (err) {
            return { assetId: asset.id.id, data: null };
          }
        });

        const allRelations = await Promise.all(relationPromises);
        if (cancelled) return;

        const relMap = {};
        allRelations.forEach((r) => {
          relMap[r.assetId] = r.data;
        });
        setRelationResponses(relMap);
      } catch (error) {
        if (!cancelled) {
          setAssetSearchError('Error fetching asset info.');
        }
      } finally {
        if (!cancelled) {
          setAssetSearchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedAssetSearch, searchType, userAuthority]);

  const handleFetchDevices = useCallback(
    (serialNumber, isLoading, page) => {
      // ComplexTable passes 3 parameters, we need to extract the page number
      let pageNum = 0;

      // The page parameter is the third argument
      if (typeof page === 'number') {
        pageNum = page;
      } else if (typeof isLoading === 'number') {
        // Fallback in case parameters are shifted
        pageNum = isLoading;
      } else {
        pageNum = 0;
      }

      console.log('Fetching devices - Page:', pageNum, 'Search:', serialNumber);

      setCurrentPage(pageNum);

      // Handle asset search
      if (searchType === 'asset' && assetSearch) {
        // Asset search is handled by useEffect, not by this function
        return;
      }

      // Handle device search
      const searchValue = serialNumber || currentSearchValue || '';
      fetchAllDevices(searchValue, pageNum);
    },
    [fetchAllDevices, searchType, assetSearch, currentSearchValue],
  );

  return (
    <Box ml={sidebarWidth} transition="margin-left 0.3s ease" p={6} ms={0}>
      <BreadCrumb items={breadcrumbItems} sidebarWidth={sidebarWidth} />
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Heading size="lg" mb={6} ml={sidebarWidth}>
        All Devices
      </Heading>
      <SimpleGrid
        ml={sidebarWidth}
        columns={{ base: 1, md: 3, lg: 5, '2xl': 5 }}
        gap="20px"
        mb="20px"
      >
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon as={MdDevices} color={brandColor} w="32px" h="32px" />
              }
            />
          }
          name="Total Devices"
          value={totalDevices}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon as={MdCheckCircle} color="green.400" w="32px" h="32px" />
              }
            />
          }
          name="Active Devices"
          value={totalActiveDevices}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdCancel} color="red.400" w="32px" h="32px" />}
            />
          }
          name="Inactive Devices"
          value={totalInactiveDevices}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={<Icon as={MdAlarm} color="orange.400" w="32px" h="32px" />}
            />
          }
          name="Warranty Expired"
          value={warrantyExpired}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon as={MdCheckCircle} color="blue.400" w="32px" h="32px" />
              }
            />
          }
          name="Warranty Active"
          value={warrantyActive}
        />
      </SimpleGrid>

      <Flex gap={6} direction={{ base: 'column', lg: 'row' }} w="100%">
        <Box flex="3" ml={sidebarWidth}>
          <ComplexTable
            columnsData={columns}
            tableData={filteredDevices}
            loading={loadingDevices}
            fetchAllDevices={handleFetchDevices}
            hasNext={hasNext}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSearch={handleTableSearch}
            searchType={searchType}
            onSearchTypeChange={setSearchType}
            showSelect={true}
          />
        </Box>
      </Flex>

      <EditDeviceDetailModal
        initialData={selectedDevice || {}}
        isOpen={isOpen}
        onClose={onClose}
        onSave={handleSave}
      />

      <GlobalModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={
          historyDevice
            ? `History for ${historyDevice.serialNumber || 'Device'}`
            : 'Device History'
        }
        size="6xl"
        minWidth="1200px"
      >
        <Box>
          <ComplexTable
            columnsData={historyColumns}
            tableData={historyData}
            loading={false}
            enableLocalPagination={true}
            pageSize={10}
            title="History"
            showSearchBar={false}
          />
        </Box>
      </GlobalModal>

      <Flex
        as="footer"
        position="fixed"
        left={0}
        bottom="0"
        width="100%"
        bg={useColorModeValue('gray.100', 'navy.800')}
        color={useColorModeValue('gray.600', 'gray.200')}
        py={2}
        px={4}
        align="center"
        justify="space-between"
        zIndex={20}
        borderTop="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
      >
        <Text fontSize="sm" fontWeight="medium">
          Version 1.0.0
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          UGX Systems
        </Text>
      </Flex>
    </Box>
  );
}
